import PropTypes from 'prop-types'

/**
 * Key Insights Cards Component
 *
 * Displays high-impact visual cards extracted from AI narrative data:
 * - Top Performers (green accent)
 * - Critical Issues / Needs Attention (red accent)
 * - Key Opportunities (blue accent)
 */
function KeyInsightsCards({ narrativeData, facilityData }) {
  if (!narrativeData) return null

  const story = narrativeData.stories?.['healthcare-access']
  if (!story) return null

  // Extract insights from narrative data
  const topPerformers =
    story.sections?.subcounty_comparison?.top_performers || []
  const needsAttention =
    story.sections?.subcounty_comparison?.needs_attention || []
  const criticalGaps =
    story.sections?.infrastructure_readiness?.critical_gaps || []

  // Get key insight text if available
  const keyInsight = story.sections?.subcounty_comparison?.key_insight || null

  // Don't render if no insights available
  if (
    topPerformers.length === 0 &&
    needsAttention.length === 0 &&
    criticalGaps.length === 0 &&
    !keyInsight
  ) {
    return null
  }

  return (
    <div className="mb-8 space-y-4">
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
        <span className="text-2xl">💡</span>
        Key Insights at a Glance
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Top Performers Card */}
        {topPerformers.length > 0 && (
          <div className="bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-300 rounded-lg p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">🌟</span>
              <div>
                <h3 className="text-lg font-semibold text-emerald-900">
                  Top Performers
                </h3>
                <p className="text-xs text-emerald-700 mt-1">
                  Leading locations in service delivery
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {topPerformers.slice(0, 3).map((location, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 bg-white bg-opacity-60 rounded px-3 py-2"
                >
                  <span className="text-emerald-600 font-bold text-sm">
                    #{index + 1}
                  </span>
                  <span className="text-gray-900 text-sm font-medium">
                    {location}
                  </span>
                </div>
              ))}
            </div>
            {topPerformers.length > 3 && (
              <p className="text-xs text-emerald-700 mt-3 italic">
                +{topPerformers.length - 3} more performing well
              </p>
            )}
          </div>
        )}

        {/* Critical Issues Card */}
        {(needsAttention.length > 0 || criticalGaps.length > 0) && (
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-lg p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">⚠️</span>
              <div>
                <h3 className="text-lg font-semibold text-red-900">
                  Critical Issues
                </h3>
                <p className="text-xs text-red-700 mt-1">
                  Areas requiring immediate attention
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {/* Locations needing attention */}
              {needsAttention.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-red-800 mb-2">
                    Low-performing locations:
                  </p>
                  <div className="space-y-1">
                    {needsAttention.slice(0, 2).map((location, index) => (
                      <div
                        key={index}
                        className="bg-white bg-opacity-60 rounded px-3 py-1.5 text-sm text-gray-900"
                      >
                        {location}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Critical infrastructure gaps */}
              {criticalGaps.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-red-800 mb-2">
                    Infrastructure gaps:
                  </p>
                  <div className="space-y-1">
                    {criticalGaps.slice(0, 2).map((gap, index) => (
                      <div
                        key={index}
                        className="bg-white bg-opacity-60 rounded px-3 py-1.5"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-900 capitalize">
                            {gap.category?.replace(/_/g, ' ')}
                          </span>
                          <span className="text-xs font-bold text-red-700">
                            {gap.coverage}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Key Opportunity Card */}
        {keyInsight && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">🎯</span>
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  Key Opportunity
                </h3>
                <p className="text-xs text-blue-700 mt-1">
                  Strategic insight for improvement
                </p>
              </div>
            </div>
            <div className="bg-white bg-opacity-60 rounded-lg p-4">
              <p className="text-sm text-gray-800 leading-relaxed">
                {keyInsight}
              </p>
            </div>
          </div>
        )}

        {/* Fallback: Show service coverage stats if no key insight */}
        {!keyInsight && facilityData?.metrics && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-3">
              <span className="text-3xl">📊</span>
              <div>
                <h3 className="text-lg font-semibold text-blue-900">
                  Performance Summary
                </h3>
                <p className="text-xs text-blue-700 mt-1">
                  Overall service metrics
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="bg-white bg-opacity-60 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-700">
                    Total Facilities
                  </span>
                  <span className="text-xl font-bold text-blue-900">
                    {facilityData.metrics.totalFacilities}
                  </span>
                </div>
              </div>
              {facilityData.metrics.serviceCoverage?.length > 0 && (
                <div className="bg-white bg-opacity-60 rounded-lg p-3">
                  <div className="text-xs text-gray-600 mb-2">Top Service:</div>
                  <div className="text-sm font-medium text-gray-900">
                    {facilityData.metrics.serviceCoverage[0].name}
                  </div>
                  <div className="text-xs text-blue-700">
                    {facilityData.metrics.serviceCoverage[0].percentage}%
                    coverage
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

KeyInsightsCards.propTypes = {
  narrativeData: PropTypes.shape({
    stories: PropTypes.object,
  }),
  facilityData: PropTypes.shape({
    metrics: PropTypes.object,
  }),
}

export default KeyInsightsCards
